/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import {
  Table,
  IconButton as IconButtonWithText,
  DeleteConfirmationModal,
  Loader,
  DropdownButton,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import {
  AddPlusIcon,
  DeleteIcon,
  // DownloadIcon,
  EditIcon,
  EyeIcon,
} from '../../../Components/icons';
import PremiseService from '../../../services/bioplus-services/premise';
import { CompanyContainer, TableCell } from './premise.style';
import moment from 'moment';
import { AddPremiseModal, EditPremiseModal } from './modals';
import { IntlMessages } from '../../../helper/langHelper';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import ViewAssignManagerToPremise from './modals/viewAssignManagerToPremise';

const listPremises = async (viewAll, companyId, filters, page, pageSize) => {
  if (viewAll && companyId) {
    return await PremiseService.listByCompany(companyId, filters, page, pageSize);
  } else if (companyId) {
    return await PremiseService.listManagedByCompany(companyId, filters, page, pageSize);
  }
};

// helper function for formatting the downloadable premises data
export function extractData(dataArray) {
  if (dataArray) {
    const extractedData = dataArray?.premises?.map((item) => {
      return {
        premiseName: item?.propertyName,
        propertyId: item?.propertyId ?? '--',
        address: `${item?.addressStreet}, ${item?.addressTown}, ${item?.addressState}` ?? '--',
        contactNumber: item?.contactNumber ?? '--',
        // premiseRange: item?.premiseRange ?? '--',
        contactEmail: item?.contactEmail ?? '--',
        createdAt: moment(item?.createdAt).format('DD-MM-YYYY'),
      };
    });
    return { bioplusUsers: extractedData };
  }
}

const Premises = () => {
  const [editPremise, setEditPremise] = useState(undefined);
  const [deletePremise, setDeletePremise] = useState();
  const [nameFilter, setNameFilter] = useState();
  const [viewAll, setViewAll] = useState(false);
  const [showAddPremise, setShowAddPremise] = useState(false);
  const [showPremiseManager, setShowPremiseManager] = useState(undefined);
  const userContext = useUserContext();
  const userRole = userContext.company;
  const companyId = userRole.companyId?.id;
  const roleId = userContext?.company?._id;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const queryClient = useQueryClient();

  const query = useQuery(['premises', viewAll, companyId, nameFilter, page, pageSize], () =>
    listPremises(viewAll, companyId, { name: nameFilter }, page, pageSize)
  );

  const allPremises = useQuery(['allPremises'], () => PremiseService.listAllPremises(companyId));

  const deleteMutation = useMutation((premise) => PremiseService.remove(premise), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('premises');
      setShowAddPremise(undefined);
    },
    onError: (error) => {
      alert(error?.response?.data?.message);
      setShowAddPremise(undefined);
    },
  });

  const tableData = query?.data?.premises ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  // Role based permission check for the "premise" module
  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'premise');

  const canManagePremise = !!isUserAllowToManage?.isManage;
  const canDownloadReport = !!isUserAllowToManage?.isReport;
  const canCreatePremise = !!isUserAllowToManage?.isCreate;

  //   table coulmns
  const userColumns = useMemo(() => {
    const cols = [
      {
        Header: <IntlMessages id="table.col.premise.name" />,
        accessor: 'propertyName',
        width: canManagePremise ? '15%' : '20%',
      },
      {
        Header: <IntlMessages id="table.col.property.id" />,
        accessor: 'propertyId',
        width: '13%',
        Cell: (cellData) => (
          <TableCell>{`${
            cellData?.row?.original?.propertyId ? cellData?.row?.original?.propertyId : '--'
          }`}</TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.premise.address" />,
        accessor: 'addressState',
        width: '20%',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.addressStreet}, ${cellData?.row?.original?.addressTown}, ${cellData?.row?.original?.addressState}`}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.premise.person.contact" />,
        accessor: 'contactNumber',
        width: '15%',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      },
      // {
      //   Header: <IntlMessages id="features.premise.range" />,
      //   accessor: 'premiseRange',
      //   width: '10%',
      //   Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      // },
      {
        Header: <IntlMessages id="table.col.creator" />,
        accessor: 'contactEmail',
        width: '15%',
        Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        width: '12%',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
    ];

    if (canManagePremise) {
      cols.push({
        Header: <IntlMessages id="table.col.actions" />,
        accessor: 'actions',
        isAction: true,
        width: '20%',
      });
    }

    return cols;
  }, [canManagePremise]);

  const handleClosedEdit = () => {
    setEditPremise(undefined);
  };
  const handleClosedDeletePremise = () => {
    setDeletePremise(undefined);
  };
  const handleDeletePremise = (premise) => {
    deleteMutation.mutate(premise);
    setDeletePremise(undefined);
  };

  // Edit/Delete actions require manage permission, the "view manager" (eye) action is always available
  const actionList = [
    ...(canManagePremise
      ? [
          (data) => (
            <IconButton onClick={() => setEditPremise(data)}>
              <EditIcon style={{ width: '18px', height: '18px' }} />
            </IconButton>
          ),
          (data) => (
            <IconButton onClick={() => setDeletePremise(data)}>
              <DeleteIcon style={{ width: '19px', height: '19px' }} />
            </IconButton>
          ),
        ]
      : []),
    // (data) => (
    //   <IconButton onClick={() => setShowPremiseManager(data)}>
    //     <EyeIcon style={{ width: '22px', height: '22px' }} />
    //   </IconButton>
    // ),
  ];

  const excelFormatData = extractData(allPremises.data);
  const fileName = `Properties-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.xlsx`;
  const fileNameCsv = `Properties-${userRole?.companyId?.name}-${moment().format(
    'DD-MM-YYYY-hh.mm A'
  )}.csv`;

  const columnConfig = [
    { header: 'Property Name', key: 'premiseName', width: 30 },
    { header: 'Property ID', key: 'propertyId', width: 30 },
    { header: 'Address', key: 'address', width: 30 },
    { header: 'Contact Number', key: 'contactNumber', width: 30 },
    // { header: 'Premise Range', key: 'premiseRange', width: 30 },
    { header: 'Contact Email', key: 'contactEmail', width: 30 },
    { header: 'Created Date', key: 'createdAt', width: 30 },
  ];

  const handleClickItem = (item) => {
    // Guard: even if triggered programmatically, block export without report permission
    if (!canDownloadReport) return;

    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  if (query.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.premises" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '10px' }}>
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                // { key: 'pdf', title: 'Export as PDF', icon: <PictureAsPdfIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
          {canCreatePremise && (
            <IconButtonWithText
              text={<IntlMessages id="button.text.addPremise" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setShowAddPremise(true);
              }}
            />
          )}
        </TwoColumns>
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={tableData}
          columns={userColumns}
          hasFooter={true}
          actions={actionList}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={query?.data?.totalPremises || 0}
        />
      </TableWrapper>
      {showAddPremise && canCreatePremise && (
        <AddPremiseModal
          onClose={() => setShowAddPremise(false)}
          companyId={companyId}
          roleId={roleId}
        />
      )}
      {editPremise && editPremise !== undefined && canManagePremise && (
        <EditPremiseModal premise={editPremise} onClose={handleClosedEdit} userRole="owner" />
      )}
      {deletePremise && canManagePremise && (
        <DeleteConfirmationModal
          onClose={handleClosedDeletePremise}
          message={
            <IntlMessages
              id="delete.comfirmation.text"
              values={{ premise: deletePremise.propertyName }}
            />
          }
          onConfirm={() => handleDeletePremise(deletePremise)}
        />
      )}
      {showPremiseManager && showPremiseManager !== undefined && (
        <ViewAssignManagerToPremise
          data={showPremiseManager}
          onClose={() => setShowPremiseManager(undefined)}
        />
      )}
    </StyledContainer>
  );
};

export default Premises;
